// ======================== React & Styles ====================
import React, { memo, useCallback } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import {
  setGlobalLoaderAction,
  setHighlightMessageAction,
  setMessagesInStoreAction,
  setReplyMessageIdAction,
} from '../../../../redux/actions/collaboration';
import { layout } from '../../../../config/styles';
// ======================== APOLLO ============================
import messageDeleteMutation from './apollo/messageDeleteMutation';
import getMessagesAfter from '../../apollo/getMessagesAfter';
import { useApolloClient, useMutation } from '@apollo/client';
// ======================== Components ========================
import { Dropdown } from 'semantic-ui-react';
import InsightMenu from './InsightMenu';
import TaskMenu from './TaskMenu';
// ======================== Utils =============================
import * as MessageMenuUI from './MessageMenuUI';
import toastMessage from '../../../../utils/toastMessage';
import {
  IMessageDeleteMutationMutation,
  IMessageDeleteMutationMutationVariables,
} from '../../../../utils/apollo/graphqlSchema';
import { SvgIcon } from '../../../Icon';

interface IProps {
  message: any;
  setIsMessageDeleted: (flag: boolean) => void;
  isOwnMessage: boolean;
}

// if not own message and not in main channel, disable message menu.
// if DIRECT channel, author able to delete message only.
// ELSE in main channel, user able to tag, reply and delete.
export default memo(function MessageMenu({
  message,
  setIsMessageDeleted,
  isOwnMessage,
}: IProps) {
  const dispatch = useDispatch();
  const client = useApolloClient();

  const mainChannelId = useSelector(
    (state: any) => state.collaboration.mainChannelId,
  );
  const currentChannelId = useSelector(
    (state: any) => state.collaboration.currentChannelId,
  );

  const [deleteMessage] = useMutation<
    IMessageDeleteMutationMutation,
    IMessageDeleteMutationMutationVariables
  >(messageDeleteMutation, {
    onCompleted: () => setIsMessageDeleted(true),
  });

  //  -------------- Find dropdown menu position -------------------
  const target = document.querySelector(
    `#message-${message.id}> .message-content`,
  );

  let right: number = 0;
  let top: number | undefined;
  let bottom: number | undefined;
  // using right cornor of message block to find dropdown menu.
  if (target) {
    const OutterContainer: any = target.closest('.collaboration-container');
    const pos: DOMRect | ClientRect = target.getBoundingClientRect();
    const topOffset = 30;
    // right = right cornor - menu width - space between button and right cornor
    right = pos.right - 140 - 4;
    // top = top + button space
    pos.top + 300 + topOffset >=
    OutterContainer.clientHeight + layout.headerHeight
      ? (bottom = OutterContainer.clientHeight + layout.headerHeight - pos.top)
      : (top = pos.top + topOffset);
  }

  const scrollToParentMessage = useCallback(async () => {
    const messageId = message.parent;
    const targetMessage = document.getElementById(`message-${messageId}`);

    if (!targetMessage) {
      dispatch(setGlobalLoaderAction(true));
      const getMessageAfterRes = await client.query({
        query: getMessagesAfter,
        variables: {
          channelId: currentChannelId,
          messageId,
        },
      });
      const messages = getMessageAfterRes?.data?.messagesAfter?.messages;
      // if target message id is not in message list. the target message is deleted
      if (messages && messages[0].id !== messageId) {
        dispatch(setGlobalLoaderAction(false));
        toastMessage({
          message: 'The original message was deleted by the author.',
          type: 'info',
        });
        // Return the scroll to the original message.
        const currentMessage = document.getElementById(`message-${message.id}`);
        currentMessage?.scrollIntoView({ behavior: 'auto' });
      } else {
        batch(() => {
          dispatch(setMessagesInStoreAction(messages));
          dispatch(setGlobalLoaderAction(false));
        });
      }
    }
    dispatch(setHighlightMessageAction(messageId));
  }, [dispatch, client, currentChannelId, message]);

  const setReplyMessageId = useCallback(() => {
    dispatch(setReplyMessageIdAction(message.id));
  }, [dispatch, message]);

  const deleteMessageCallback = useCallback(() => {
    deleteMessage({
      variables: { input: { id: message.id } },
    });
  }, [deleteMessage, message]);

  if (currentChannelId === mainChannelId || isOwnMessage) {
    return (
      <MessageMenuUI.Menu
        trigger={
          <SvgIcon
            name="dots-three-outline-vertical"
            width={16}
            height={16}
            isHoverable={true}
          />
        }
        icon={null}
        pointing="top right"
        right={right}
        top={top}
        bottom={bottom}
      >
        <Dropdown.Menu>
          {/* reply message */}
          {currentChannelId === mainChannelId && (
            <>
              <MessageMenuUI.DropdownItem
                text="Reply"
                onClick={setReplyMessageId}
              />
              {/* scroll to parent message */}
              {message.parent && (
                <MessageMenuUI.DropdownItem
                  text="Show Original"
                  onClick={scrollToParentMessage}
                />
              )}
              {isOwnMessage && <Dropdown.Divider />}
            </>
          )}

          {/* delete message if user's own message */}
          {isOwnMessage && (
            <MessageMenuUI.DropdownItem
              text="Delete"
              onClick={deleteMessageCallback}
            />
          )}
          {/* if in main channel, allow task and insight tag */}
          {currentChannelId === mainChannelId && (
            <>
              <InsightMenu message={message} />
              <TaskMenu message={message} />
            </>
          )}
        </Dropdown.Menu>
      </MessageMenuUI.Menu>
    );
  }

  return <></>;
});
